import { BaseStore } from "@omni/types/enums/baseStore.enum";
import { Country } from "@omni/types/enums/country.enum";
import { Environment } from "@omni/types/enums/environment.enum";
import { FeatureToggleKey } from "@omni/types/enums/featureToggleKey.enum";
import { Locale } from "@omni/types/enums/locale.enum";
import { PageTemplate } from "@omni/types/enums/pageTemplate.enum";
import { DeepPartial } from "@omni/types/utils/deepPartial";
import { ModelMapper } from "@omni/types/utils/modelMapper";
import { getLocale } from "@omni/utils/locale.utils";
import { fromEntries, values } from "@omni/utils/object.utils";
import * as yup from "yup";
import { Schema } from "yup";

import { LanguageSchema, RawLanguage, toLanguage } from "./language.schema";

const XF_PAGE_TEMPLATE = "experience-fragment-web-variation";

export type RawComponentsConfig = Omit<
    ComponentsConfig,
    "general" | "languages"
> & {
    general: Omit<
        ComponentsConfig["general"],
        "basestore-name" | "basestore" | "locale" | "page-template" | "author"
    > & {
        "basestore-name": BaseStore;
        "basestore": string;
        "locale": string;
        "page-template": string;
        "author": boolean | undefined;
    };
    languages: Array<RawLanguage>;
};

export const RawComponentsConfigSchema = yup.object<
    DeepPartial<RawComponentsConfig>
>({
    "general": yup.object().required(),
    "integrations": yup.object().required(),
    "languages": yup.array().required(),
    "pages": yup.object().required(),
    "external-links": yup.object().required(),
    "feature-toggles": yup.object().required()
}) as Schema<DeepPartial<RawComponentsConfig>>;

const toComponentsConfig: ModelMapper<
    DeepPartial<RawComponentsConfig>,
    DeepPartial<ComponentsConfig>
> = (config) => {
    // XF cannot have a page template, but this cannot be set to null in AEM
    const rawPageTemplate = config?.general?.["page-template"];
    const pageTemplate =
        rawPageTemplate === XF_PAGE_TEMPLATE
            ? undefined
            : (values(PageTemplate).find(
                  (template) => template === rawPageTemplate
              ) ?? PageTemplate.Content);

    return {
        ...config,
        general: {
            ...config.general,
            basestore: config?.general?.["basestore-name"],
            "basestore-name": config?.general?.["basestore"],
            locale: getLocale(config?.general?.locale ?? ""),
            "page-template": pageTemplate,
            "author": config?.general?.["author"] ?? false
        },
        languages: config.languages?.map(toLanguage)
    };
};

export const ComponentsConfigSchema = yup
    .object()
    .transform((_, rawConfig) => {
        return toComponentsConfig(rawConfig);
    })
    .shape({
        "general": yup
            .object({
                "api-base-url": yup.string().required(),
                "bff-base-url": yup.string().required(),
                "environment": yup
                    .mixed<Environment>()
                    .oneOf(values(Environment))
                    .required(),
                "basestore-name": yup.string().required(),
                "basestore": yup
                    .mixed<BaseStore>()
                    .oneOf(values(BaseStore))
                    .required(),
                "country": yup
                    .mixed<Country>()
                    .oneOf(values(Country))
                    .required(),
                "locale": yup.mixed<Locale>().oneOf(values(Locale)).required(),
                "fallback-image-path": yup.string().defined().strict(true),
                "authentication-required": yup.boolean().required(),
                "maximum-order-quantity": yup.number().required(),
                "page-template": yup
                    .mixed<PageTemplate>()
                    .oneOf(values(PageTemplate))
                    .optional(),
                "dam-path": yup.string().required(),
                "author": yup.boolean().required()
            })
            .default(undefined)
            .required(),
        "integrations": yup
            .object({
                "mollie-profile-id": yup.string().required(),
                "mollie-test-mode": yup.boolean().required(),
                "google-maps-site-lat": yup.number().required(),
                "google-maps-site-long": yup.number().required(),
                "google-captcha-site-key": yup.string().required(),
                "neverbounce-api-key": yup.string().required(),
                "neverbounce-timeout": yup.number().required(),
                "bpost-api-key": yup.string().required(),
                "bpost-url-validation": yup.string().required(),
                "bpost-url-autocomplete": yup.string().required(),
                "woosmap-key": yup.string().required(),
                "aiden-key": yup.string().optional(),
                "algolia-index-product": yup.string().required(),
                "klarna-client-id": yup.string().optional(),
                "klarna-client-src": yup.string().optional()
            })
            .default(undefined)
            .required(),
        "languages": yup.array(LanguageSchema).required(),
        "pages": yup
            .object({
                "cart": yup.string().required(),
                "compare": yup.string().required(),
                "shared-wishlist": yup.string().required(),
                "stores": yup.string().required(),
                "wishlist": yup.string().required(),
                "home": yup.string().required(),
                "giftcard": yup.string().required(),
                "login": yup.string().required(),
                "request-password": yup.string().required(),
                "checkout-login": yup.string().required(),
                "checkout-account": yup.string().required(),
                "checkout-payment": yup.string().required(),
                "checkout-confirmation": yup.string().required(),
                "search": yup.string().required()
            })
            .default(undefined)
            .required(),
        "external-links": yup
            .object({
                "plant-protection-products": yup.string().optional(),
                "repairability-index": yup.string().optional(),
                "guarantee-notice": yup.string().optional()
            })
            .default(undefined)
            .required(),
        "feature-toggles": yup
            .object(
                fromEntries(
                    values(FeatureToggleKey).map((key) => [
                        key,
                        yup.boolean().optional()
                    ])
                )
            )
            .default(undefined)
            .required()
    });

export type ComponentsConfig = yup.InferType<typeof ComponentsConfigSchema>;
